import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, XCircle, Upload, Calendar, Building2, Layers, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const ScoreIndicator = ({ score }) => {
  const color = score >= 90 ? 'text-success' : score >= 70 ? 'text-warning' : 'text-danger';
  const Icon = score >= 90 ? CheckCircle : score >= 70 ? AlertTriangle : XCircle;
  return (
    <div className='flex items-center gap-2'>
      <Icon size={16} className={color} strokeWidth={1.5} />
      <span className={`font-bold ${color}`}>{score}%</span>
    </div>
  );
};

/**
 * Completeness verification dashboard (Screen 17).
 * Financial reconciliation, temporal/facility/category coverage.
 * @param {object} props
 * @param {object} [props.completenessData] - results from completeness check API
 * @param {Function} [props.onUploadFinancialSummary]
 * @param {Function} [props.onRunCheck]
 */
const CompletenessVerification = ({ completenessData, onUploadFinancialSummary, onRunCheck }) => {
  const [uploading, setUploading] = useState(false);

  const data = completenessData;
  const hasData = !!data;

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between'>
        <h2 className='text-lg font-semibold flex items-center gap-2'>
          <CheckCircle size={20} strokeWidth={1.5} />
          Completeness Verification
        </h2>
        {hasData && (
          <div className='flex items-center gap-2'>
            <span className='text-sm text-muted-foreground'>Overall:</span>
            <ScoreIndicator score={data.overallCompleteness || 0} />
          </div>
        )}
      </div>

      {!hasData ? (
        <Card className='bg-card'>
          <CardContent className='py-12 text-center'>
            <Upload size={40} className='mx-auto mb-3 text-muted-foreground' strokeWidth={1.5} />
            <h3 className='font-semibold text-foreground mb-2'>Run Completeness Check</h3>
            <p className='text-sm text-muted-foreground mb-4 max-w-md mx-auto'>
              Verify your reported data covers all months, facilities, and emission categories.
              Optionally upload a financial summary to check for material discrepancies.
            </p>
            <div className='flex gap-3 justify-center'>
              <Button variant='outline' onClick={onUploadFinancialSummary}>
                <DollarSign size={14} strokeWidth={1.5} />
                Upload Financial Summary
              </Button>
              <Button onClick={onRunCheck}>
                Run Basic Check
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {data.temporalCoverage && (
            <Card className='bg-card'>
              <CardHeader>
                <CardTitle className='text-sm flex items-center gap-2'>
                  <Calendar size={16} strokeWidth={1.5} />
                  Temporal Coverage
                  <ScoreIndicator score={data.temporalCoverage.score} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='grid grid-cols-12 gap-1 mb-2'>
                  {Array.from({ length: 12 }, (_, i) => {
                    const month = i + 1;
                    const covered = data.temporalCoverage.monthsCovered?.includes(month);
                    return (
                      <div
                        key={month}
                        className={`h-8 rounded text-xs flex items-center justify-center font-mono ${
                          covered ? 'bg-success/20 text-success' : 'bg-danger/20 text-danger'
                        }`}
                        title={`Month ${month}: ${covered ? 'Covered' : 'Missing'}`}
                      >
                        {month}
                      </div>
                    );
                  })}
                </div>
                {data.temporalCoverage.monthsMissing?.length > 0 && (
                  <p className='text-xs text-danger'>
                    Missing: {data.temporalCoverage.monthsMissing.map((m) => `Month ${m}`).join(', ')}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {data.facilityCoverage && (
            <Card className='bg-card'>
              <CardHeader>
                <CardTitle className='text-sm flex items-center gap-2'>
                  <Building2 size={16} strokeWidth={1.5} />
                  Facility Coverage
                  <ScoreIndicator score={data.facilityCoverage.score} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm mb-2'>
                  {data.facilityCoverage.reporting} of {data.facilityCoverage.expected} facilities reporting
                </p>
                <Progress value={(data.facilityCoverage.reporting / data.facilityCoverage.expected) * 100} className='mb-2' />
                {data.facilityCoverage.missing?.length > 0 && (
                  <p className='text-xs text-danger'>
                    Missing: {data.facilityCoverage.missing.join(', ')}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {data.categoryCoverage && (
            <Card className='bg-card'>
              <CardHeader>
                <CardTitle className='text-sm flex items-center gap-2'>
                  <Layers size={16} strokeWidth={1.5} />
                  Category Coverage
                  <ScoreIndicator score={data.categoryCoverage.score} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm mb-2'>
                  {data.categoryCoverage.present} of {data.categoryCoverage.expected} categories present
                </p>
                <Progress value={(data.categoryCoverage.present / data.categoryCoverage.expected) * 100} className='mb-2' />
                {data.categoryCoverage.missing?.length > 0 && (
                  <div className='text-xs text-danger'>
                    Missing: {data.categoryCoverage.missing.join(', ')}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {data.financialReconciliation && (
            <Card className='bg-card'>
              <CardHeader>
                <CardTitle className='text-sm flex items-center gap-2'>
                  <DollarSign size={16} strokeWidth={1.5} />
                  Financial Reconciliation
                  <ScoreIndicator score={data.financialReconciliation.score} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='flex justify-between text-sm mb-2'>
                  <span>Reported: £{(data.financialReconciliation.totalReported || 0).toLocaleString()}</span>
                  <span>Expected: £{(data.financialReconciliation.totalExpected || 0).toLocaleString()}</span>
                </div>
                <Progress value={data.financialReconciliation.score} className='mb-2' />
                {data.financialReconciliation.gap > 0 && (
                  <p className='text-xs text-warning'>
                    Gap: £{data.financialReconciliation.gap.toLocaleString()} ({data.financialReconciliation.gapPct}%)
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default CompletenessVerification;
